import { useState } from "react";
import { formatPlanDate, endedReasonLabel, planAccessUntil, planRenewUrl } from "./subscriptionGate";

/**
 * Full-screen gate shown when EVERY subscription the member holds has run out.
 *
 * Deliberately not dismissible — there is no close button and the backdrop does
 * nothing, because the content behind it is content they are no longer paying
 * for. It is a gate, not a notice.
 *
 * It is NOT the enforcement point, though. flexiapi already refuses lapsed
 * content off membership_accesses.expires_at; without this overlay a member just
 * meets a wall of locked cards with no explanation. This is the explanation, and
 * the way back in.
 *
 * Three ways out, on purpose: buy a plan again (the one that matters — a sealed
 * dead end costs the creator the renewal), look at My Plans, or sign out.
 */

/** Lighten/darken a hex colour — same local helper idiom as the other modals. */
function shade(hex, percent) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const amt = Math.round(2.55 * percent);
  const r = clamp((num >> 16) + amt);
  const g = clamp(((num >> 8) & 0x00ff) + amt);
  const b = clamp((num & 0x0000ff) + amt);
  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
}

const money = (amt, cur) => {
  if (amt == null || amt === "") return "";
  const n = Number(amt);
  return Number.isNaN(n) ? `${cur} ${amt}` : `${cur} ${n.toFixed(2)}`;
};

// The platform's frequency vocabulary carries the parent's typo ("quaterly"),
// which is fine in storage but should not reach a buyer's eye.
const FREQUENCY_LABELS = {
  quaterly: "Quarterly", quarterly: "Quarterly", monthly: "Monthly",
  yearly: "Yearly", weekly: "Weekly", daily: "Daily",
  month: "Monthly", year: "Yearly", week: "Weekly", day: "Daily",
};

const planPrice = (p) => {
  const sub = p.subscription_plan;
  return p.is_subscription && sub?.amount != null && sub.amount !== ""
    ? money(sub.amount, sub.currency || p.currency)
    : money(p.price, p.currency);
};

const cycleLabel = (p) => {
  const name = p.subscription_plan?.billing_name;
  if (!name) return "";
  const tail = String(name).split("/").pop().trim().toLowerCase();
  return FREQUENCY_LABELS[tail] || tail;
};

export default function SubscriptionExpiredOverlay({
  plans = [],
  primary = "#0274F7",
  membershipName = "",
  onViewPlans,
  onSignOut,
  onRecheck,
}) {
  const [rechecking, setRechecking] = useState(false);

  // The buyer pays in the OTHER tab, so this one has to be told to look again.
  // The shell also re-checks on refocus; this is the manual way out for when a
  // gateway is slow to settle and the first look came back too early.
  const recheck = async () => {
    if (!onRecheck || rechecking) return;
    setRechecking(true);
    try {
      await onRecheck();
    } finally {
      setRechecking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-stretch justify-center overflow-y-auto bg-ink/80 p-0 backdrop-blur-md sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sub-gate-title"
    >
      <style>{`
        @keyframes gatePop { from { opacity: 0; transform: translateY(18px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>

      <div className="relative my-auto flex h-full w-full flex-col overflow-hidden rounded-none bg-surface shadow-2xl animate-[gatePop_.3s_cubic-bezier(.22,1,.36,1)] sm:h-auto sm:max-h-[94vh] sm:max-w-xl sm:rounded-3xl sm:ring-1 sm:ring-black/5">
        {/* ── Header ── */}
        <div className="relative shrink-0 overflow-hidden bg-ink px-6 pb-7 pt-8 text-center">
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{ background: `linear-gradient(160deg, ${primary}4D 0%, ${primary}C2 100%)` }}
          />
          <div
            className="relative z-10 mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-surface shadow-md"
            style={{ color: primary }}
          >
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V7.5a4.5 4.5 0 10-9 0v3M6.75 10.5h10.5a1.5 1.5 0 011.5 1.5v7.5a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5V12a1.5 1.5 0 011.5-1.5z" />
            </svg>
          </div>
          <h2
            id="sub-gate-title"
            className="relative z-10 mt-4 text-xl font-bold text-white"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
          >
            Your access has ended
          </h2>
          <p
            className="relative z-10 mx-auto mt-1 max-w-sm text-sm text-white/95"
            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
          >
            {plans.length === 1
              ? "Your subscription has run out, so this content is locked."
              : `All ${plans.length} of your subscriptions have run out, so this content is locked.`}
            {" "}Renew below to pick up where you left off.
          </p>
        </div>

        {/* ── The lapsed plans ── */}
        <div className="flex-1 overflow-y-auto scrollbar-hide bg-gray-50/50 px-5 py-5 sm:px-6">
          <ul className="space-y-3.5">
            {plans.map((p) => (
              <li
                key={p.id}
                className="relative overflow-hidden rounded-2xl bg-surface p-4 pl-5 ring-1 ring-gray-100 shadow-sm"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 w-[3px] bg-gray-200"
                />
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-[15px] font-bold leading-tight text-gray-900">
                        {p.title}
                      </h3>
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200/70 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30">
                        {endedReasonLabel(p)}
                      </span>
                    </div>
                    {planAccessUntil(p) && (
                      <p className="mt-1.5 text-[12px] font-medium text-gray-500">
                        Access ended {formatPlanDate(planAccessUntil(p))}
                      </p>
                    )}
                  </div>

                  <div className="shrink-0 text-right">
                    {planPrice(p) && (
                      <p className="text-[15px] font-extrabold leading-none" style={{ color: primary }}>
                        {planPrice(p)}
                      </p>
                    )}
                    {cycleLabel(p) && (
                      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        {cycleLabel(p)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Only rendered when the product actually has somewhere to buy —
                    getMyplans sends buy_url:'' when it has neither a sales nor a
                    checkout page, and a button that goes nowhere is worse than
                    no button. Same rule the locked-lesson CTA follows.

                    Labelled "Renew" rather than "Buy now": every plan on this
                    screen is one the member already held, so it is a renewal to
                    them even though it is the same purchase to the gateway. */}
                {planRenewUrl(p) ? (
                  <a
                    href={planRenewUrl(p)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3.5 flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${shade(primary, -18)} 100%)` }}
                  >
                    Renew
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </a>
                ) : (
                  <p className="mt-3 text-[11.5px] text-gray-400">
                    Contact {membershipName || "the creator"} to renew this plan.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Already renewed? ── */}
        {onRecheck && (
          <div className="shrink-0 border-t border-gray-100 bg-gray-50/50 px-6 py-3 text-center">
            <button
              type="button"
              onClick={recheck}
              disabled={rechecking}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
            >
              <svg
                className={`h-3.5 w-3.5 ${rechecking ? "animate-spin" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 11A8 8 0 006.3 6.3L4 8.5M4 4v4.5h4.5M4 13a8 8 0 0013.7 4.7L20 15.5M20 20v-4.5h-4.5" />
              </svg>
              {rechecking ? "Checking…" : "Already renewed? Check again"}
            </button>
          </div>
        )}

        {/* ── Footer: the other two ways out ── */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-surface px-6 py-4">
          <button
            type="button"
            onClick={onSignOut}
            className="rounded-xl px-3 py-2 text-[13px] font-semibold text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={onViewPlans}
            className="rounded-xl px-4 py-2 text-[13px] font-semibold transition hover:bg-gray-100"
            style={{ color: primary }}
          >
            View My Plans
          </button>
        </div>
      </div>
    </div>
  );
}
